import { isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { NavigationError, Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { User } from 'avn/models/user';
import { SharedCompsService } from 'avn/shared/shared-comps.service';
import { filter } from 'rxjs';
import { environment } from 'src/environments/environment';

import { AuthAppService } from './auth-app/auth-app.service';

const GOOGLE_MAPS_PLACES_LOAD_ERROR = 'Could not load "places"';
const GOOGLE_MAPS_ZERO_RESULTS_STATUS = 'ZERO_RESULTS';

// Lazy chunk import failures per browser: Safari, Chrome, Firefox. Usually a tab opened before a deploy
// asking for a hashed chunk that no longer exists.
const CHUNK_LOAD_ERROR_PATTERN =
  /Importing a module script failed|Failed to fetch dynamically imported module|error loading dynamically imported module/i;
const CHUNK_RELOAD_STORAGE_KEY = 'avn-chunk-reload-at';
// A second chunk failure within this window means a reload didn't help, so report it instead of looping.
const CHUNK_RELOAD_COOLDOWN_MS = 30_000;

@Injectable()
export class ErrorInterceptor implements ErrorHandler {
  constructor(
    private router: Router,
    private authService: AuthAppService,
    private shared: SharedCompsService,
    @Inject(PLATFORM_ID) private platformId: any
  ) {
    if (isPlatformBrowser(this.platformId)) {
      // The router emits NavigationError before the rejected navigation reaches handleError.
      this.router.events
        .pipe(filter((event): event is NavigationError => event instanceof NavigationError))
        .subscribe((event) => {
          if (this.isChunkLoadError(event.error)) {
            this.reloadForChunkLoadError(event.url);
          }
        });
    }
  }

  private chunkReloadPending = false;

  handleError(error: any) {
    const normalizedError: any =
      error instanceof HttpErrorResponse ? error : error?.rejection || error?.originalError || error;
    if (normalizedError === 'Not logged in') {
      return;
    }
    if (this.chunkReloadPending && this.isChunkLoadError(normalizedError)) {
      return;
    }
    const requestUrl = (normalizedError?.url || normalizedError?.error?.url || '').toString();
    const isBlogApiRequest = requestUrl.includes('/blog/wp-json/');
    const errorMessage = this.extractMessage(normalizedError);
    const rawErrorMessage = this.extractMessage(error);
    const isLoginRedirectUrl = this.router.url.startsWith('/login') && this.router.url.includes('redirect=');
    const isExpectedAuthRedirectRejection =
      isLoginRedirectUrl &&
      (errorMessage === 'Not logged in' ||
        rawErrorMessage.includes('Uncaught (in promise): Not logged in') ||
        rawErrorMessage.includes('Uncaught (in promise): Error: Not logged in'));

    if (requestUrl.includes('validate-token')) {
      return;
    }

    // Ignore known noisy browser/network failures for blog WP endpoints.
    // These usually surface as status 0 (blocked/CORS/network) and are not actionable app errors.
    if (isBlogApiRequest && normalizedError?.status === 0) {
      return;
    }

    const url = this.router.url.split('/')[1];

    if (!['blog'].includes(url) && (normalizedError?.status === 401 || normalizedError?.status === 403)) {
      // // invalid session token
      this.authService.purgeAuth();
      this.authService.setloggedUser(new User());
      if (
        this.router.url.split('/')[1] !== 'login' &&
        (this.router.url.split('/')[1] === 'booking' ||
          this.router.url.split('/')[1] === 'business' ||
          this.router.url.split('/')[1] === 'wellbeing')
      ) {
        this.router.navigate(['/login'], { queryParams: { redirect: this.router.url } });
      }
    }
    if (!['blog'].includes(url) && normalizedError?.error?.message) {
      this.shared.showSnackBar(normalizedError?.error?.message, 5);
    }

    if (isExpectedAuthRedirectRejection) {
      return;
    }

    if (this.isIgnoredGoogleMapsPlacesError(normalizedError, error)) {
      return;
    }

    if (isPlatformBrowser(this.platformId)) {
      console.info(
        '[GlobalErrorHandler] Error captured on route:',
        this.router.url,
        this.getConsoleErrorSummary(normalizedError, error),
        error
      );
    }

    if (this.isThirdPartyScriptError(normalizedError, error)) {
      return;
    }

    if (this.isIgnorableSentryErrorShape(normalizedError)) {
      return;
    }

    if (
      isPlatformBrowser(this.platformId) &&
      environment.sentry.enabled &&
      !!environment.sentry.dsn &&
      this.shouldCaptureInSentry(normalizedError)
    ) {
      this.captureForSentry(normalizedError, error);
    }
  }

  private isChunkLoadError(err: any): boolean {
    return CHUNK_LOAD_ERROR_PATTERN.test(this.extractMessage(err));
  }

  // Full page load to the URL the user was navigating to, so the browser fetches the current build.
  private reloadForChunkLoadError(url: string): void {
    try {
      const lastReloadAt = Number(sessionStorage.getItem(CHUNK_RELOAD_STORAGE_KEY)) || 0;
      if (Date.now() - lastReloadAt < CHUNK_RELOAD_COOLDOWN_MS) {
        return;
      }
      sessionStorage.setItem(CHUNK_RELOAD_STORAGE_KEY, String(Date.now()));
    } catch {
      // Without storage we can't guard against a reload loop, so let the error surface normally.
      return;
    }
    this.chunkReloadPending = true;
    window.location.assign(url);
  }

  private extractMessage(err: any): string {
    if (!err) {
      return '';
    }
    if (typeof err === 'string') {
      return err;
    }
    return (err?.message || err?.error?.message || err?.toString?.() || '').toString();
  }

  private isIgnoredGoogleMapsPlacesError(normalizedError: any, originalError: any): boolean {
    return [
      normalizedError,
      originalError,
      normalizedError?.rejection,
      originalError?.rejection,
      normalizedError?.originalError,
      originalError?.originalError
    ].some((err) => this.isIgnoredGoogleMapsPlacesMessage(this.extractMessage(err)));
  }

  private isIgnoredGoogleMapsPlacesMessage(message: string): boolean {
    return (
      message.includes(GOOGLE_MAPS_PLACES_LOAD_ERROR) ||
      message === GOOGLE_MAPS_ZERO_RESULTS_STATUS ||
      message.includes(`: ${GOOGLE_MAPS_ZERO_RESULTS_STATUS}`)
    );
  }

  private isIgnorableSentryErrorShape(err: any): boolean {
    if (!err || typeof err !== 'object' || Array.isArray(err)) {
      return false;
    }

    const expectedKeys = ['error', 'headers', 'message', 'name', 'ok'];
    const keys = Object.keys(err).sort();

    return keys.length === expectedKeys.length && expectedKeys.every((key) => keys.includes(key));
  }

  private shouldCaptureInSentry(err: any): boolean {
    if (err instanceof HttpErrorResponse) {
      return this.shouldCaptureHttpError(err);
    }

    return !this.isIgnorableSentryErrorShape(err);
  }

  private shouldCaptureHttpError(err: HttpErrorResponse): boolean {
    // Status 0 = the request never got a response (offline, aborted navigation, CORS/ad-blocker).
    // Not actionable as an app error; surfaces as "HTTP unknown: Failed to fetch (<host>)".
    if (err.status === 0) {
      return false;
    }

    if (err.status >= 400 && err.status < 500) {
      return false;
    }

    return true;
  }

  private captureForSentry(normalizedError: any, originalError: any): void {
    const sentryError = this.toSentryError(normalizedError);

    Sentry.withScope((scope) => {
      scope.setTag('route', this.router.url.split('?')[0] || '/');

      if (normalizedError instanceof HttpErrorResponse) {
        scope.setTag('http_status', String(normalizedError.status || 'unknown'));
        scope.setContext('http_error', {
          message: normalizedError.message,
          responseMessage: this.extractResponseMessage(normalizedError.error),
          status: normalizedError.status,
          statusText: normalizedError.statusText,
          url: normalizedError.url || ''
        });
      }

      const originalMessage = this.extractMessage(originalError);
      if (originalMessage && originalMessage !== sentryError.message) {
        scope.setContext('original_error', { message: originalMessage });
      }

      Sentry.captureException(sentryError);
    });
  }

  private toSentryError(err: any): Error {
    if (err instanceof Error) {
      return err;
    }

    if (err instanceof HttpErrorResponse) {
      const status = err.status || 'unknown';
      const responseMessage = this.extractResponseMessage(err.error);
      const message = responseMessage || err.message || err.statusText || 'Request failed';
      return new Error(`HTTP ${status}: ${message}`);
    }

    return new Error(this.extractMessage(err) || 'Unknown non-Error exception');
  }

  private getConsoleErrorSummary(normalizedError: any, originalError: any): string {
    if (normalizedError instanceof HttpErrorResponse) {
      const status = normalizedError.status || 'unknown';
      const message = this.extractResponseMessage(normalizedError.error) || normalizedError.message || 'Request failed';
      const url = normalizedError.url ? ` (${normalizedError.url})` : '';
      return `HTTP ${status}: ${message}${url}`;
    }

    return this.extractMessage(normalizedError) || this.extractMessage(originalError) || 'Unknown error';
  }

  private extractResponseMessage(response: any): string {
    if (!response) {
      return '';
    }
    if (typeof response === 'string') {
      return response;
    }
    return (response?.message || response?.error || response?.detail || response?.Message || '').toString();
  }

  private isThirdPartyScriptError(normalizedError: any, originalError: any): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }

    const firstPartyHosts = this.getFirstPartyHostnames();
    if (!firstPartyHosts.size) {
      return false;
    }

    const sourceUrls = this.extractSourceUrls(normalizedError, originalError);
    if (!sourceUrls.length) {
      return false;
    }

    // Zone.js's own runtime (bundled in our first-party "polyfills" chunk) wraps every async
    // callback it dispatches - including callbacks from injected third-party scripts (e.g. the
    // tracking pixel in index.prod.html). That adds first-party "invoke"/"run"/"runTask" frames
    // to the stack of an otherwise purely third-party error, which would defeat the checks below.
    // Ignore those scaffolding frames when they're not the only frames present.
    const meaningfulUrls = sourceUrls.filter((url) => !this.isRuntimeScaffoldingUrl(url));
    const urlsToInspect = meaningfulUrls.length ? meaningfulUrls : sourceUrls;

    const firstSourceHost = this.getHostnameFromUrl(urlsToInspect[0]);
    if (firstSourceHost && !firstPartyHosts.has(firstSourceHost)) {
      return true;
    }

    const sourceHosts = urlsToInspect
      .map((url) => this.getHostnameFromUrl(url))
      .filter((host): host is string => !!host);

    return sourceHosts.length > 0 && sourceHosts.every((host) => !firstPartyHosts.has(host));
  }

  private isRuntimeScaffoldingUrl(url: string): boolean {
    // Stack frame URLs carry a trailing ":line:column" (e.g. ".../polyfills.<hash>.js:3:15965"),
    // so match against the raw string rather than URL.pathname, tolerating that suffix.
    return /\/polyfills(?:\.[\w-]+)?\.js(?::\d+)*(?:[?#].*)?$/i.test(url);
  }

  private getFirstPartyHostnames(): Set<string> {
    const hostnames = new Set<string>();
    const currentHostName = window?.location?.hostname;

    if (currentHostName) {
      hostnames.add(currentHostName);
    }

    return hostnames;
  }

  private extractSourceUrls(normalizedError: any, originalError: any): string[] {
    const stackSources = [
      normalizedError?.stack,
      originalError?.stack,
      normalizedError?.rejection?.stack,
      originalError?.rejection?.stack
    ]
      .filter(Boolean)
      .join('\n');

    if (!stackSources) {
      return [];
    }

    const matchedUrls = stackSources.match(/(?:https?:\/\/|file:\/\/|chrome-extension:\/\/)[^\s)]+/g) || [];
    const uniqueUrls: string[] = [];
    const seen = new Set<string>();

    matchedUrls.forEach((rawUrl) => {
      const sanitizedUrl = rawUrl.replace(/[,\];]+$/, '');
      if (!sanitizedUrl || seen.has(sanitizedUrl)) {
        return;
      }
      seen.add(sanitizedUrl);
      uniqueUrls.push(sanitizedUrl);
    });

    return uniqueUrls;
  }

  private getHostnameFromUrl(url: string): string | null {
    try {
      return new URL(url).hostname || null;
    } catch {
      return null;
    }
  }
}
